import { useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Save, Trash2, UserPlus } from "lucide-react";
import { eur } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Display, Keypad, REPARTI } from "@/components/CassaKeys";
import { ScanButton } from "@/components/BarcodeScanner";
import { DateField } from "@/components/DateField";

const nomeCli = (c) => c.denominazione || `${c.nome || ""} ${c.cognome || ""}`.trim();
const riga = (l) => l.quantita * l.prezzo * (1 - (l.sconto || 0) / 100);
const cents = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const useTastiera = () => {
  const [buffer, setBuffer] = useState("");
  const [qty, setQty] = useState(1);
  const key = (k) => {
    if (k === "C") { if (buffer) setBuffer(""); else setQty(1); return; }
    if ((buffer + k).length <= 8) setBuffer((buffer + k).replace(/^0+(?=\d)/, ""));
  };
  const times = () => { setQty(Math.max(1, parseInt(buffer || "1", 10))); setBuffer(""); };
  return { buffer, qty, key, times, reset: () => { setBuffer(""); setQty(1); } };
};

const Righe = ({ lines, ro, setQty, remove }) => (
  <div className="max-h-[340px] min-h-[220px] overflow-y-auto rounded-lg border border-sky-200 bg-white p-3 font-mono text-sm" data-testid="oc-receipt">
    {lines.length === 0 ? <p className="py-8 text-center font-sans text-slate-500">Ordine vuoto: tocca un articolo, spara un codice o digita un importo e premi un reparto</p> : lines.map((l, i) => (
      <div key={i} className="flex items-center gap-2 border-b border-dashed border-slate-200 py-1.5" data-testid={`oc-row-${i}`}>
        <div className="min-w-0 flex-1"><div className="truncate font-sans font-medium">{l.descrizione}</div>
          <div className="text-xs text-slate-500">{l.quantita} x {cents(l.prezzo)} · {Number(l.aliquota) ? `IVA ${l.aliquota}%` : l.natura}</div></div>
        {!ro && <button type="button" className="rounded bg-slate-100 p-1 hover:bg-slate-200" data-testid={`oc-row-minus-${i}`} onClick={() => setQty(i, l.quantita - 1)}><Minus className="h-3.5 w-3.5" /></button>}
        {!ro && <button type="button" className="rounded bg-slate-100 p-1 hover:bg-slate-200" data-testid={`oc-row-plus-${i}`} onClick={() => setQty(i, l.quantita + 1)}><Plus className="h-3.5 w-3.5" /></button>}
        <span className="w-20 text-right font-semibold" data-testid={`oc-row-total-${i}`}>{cents(riga(l))}</span>
        {!ro && <button type="button" className="text-slate-400 hover:text-rose-600" data-testid={`oc-row-remove-${i}`} onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></button>}
      </div>
    ))}
  </div>
);

const Articoli = ({ products, onPick }) => {
  const [q, setQ] = useState("");
  const list = products.filter((p) => !q || `${p.codice} ${p.descrizione} ${p.barcode || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2">
      <Input data-testid="oc-search" className="h-10 bg-white" placeholder="Cerca articolo" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="grid max-h-[360px] grid-cols-2 gap-2 overflow-y-auto pr-1" data-testid="oc-article-keys">
        {list.length === 0 && <p className="col-span-2 py-4 text-center text-sm text-slate-500">Nessun articolo</p>}
        {list.map((p) => (
          <button key={p.id} type="button" data-testid={`oc-article-${p.id}`} onClick={() => onPick(p)}
            className="flex h-16 flex-col items-center justify-center rounded-lg border border-yellow-400 bg-yellow-300 px-2 text-center text-sm font-semibold text-slate-900 shadow-sm transition-transform hover:bg-yellow-200 active:scale-95">
            <span className="line-clamp-2 leading-tight">{p.descrizione}</span><span className="font-mono text-xs">{eur(p.prezzo)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const Testata = ({ f, setF, custs, ro, onNewCust }) => (
  <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
    <div className="flex gap-2 sm:col-span-2">
      <Select disabled={ro} value={f.customer_id || undefined} onValueChange={(v) => setF({ ...f, customer_id: v })}>
        <SelectTrigger data-testid="order-customer-select" className="h-11"><SelectValue placeholder="Scegli il cliente" /></SelectTrigger>
        <SelectContent>{custs.map((c) => <SelectItem key={c.id} value={c.id}>{nomeCli(c)}</SelectItem>)}</SelectContent>
      </Select>
      {!ro && <Button variant="outline" className="h-11 shrink-0" data-testid="order-new-customer-button" onClick={onNewCust}><UserPlus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Nuovo</span></Button>}
    </div>
    <DateField disabled={ro} className="h-11" testid="oc-date-input" value={f.data} onChange={(v) => setF({ ...f, data: v })} />
    <Input disabled={ro} className="h-11" placeholder="Consegna presso (facoltativo)" data-testid="order-delivery-input" value={f.consegna} onChange={(e) => setF({ ...f, consegna: e.target.value })} />
  </div>
);

export const OrdineCassa = ({ f, setF, custs, products, ro, busy, save, addLine, onNewCust, scan }) => {
  const kb = useTastiera();
  const [ultima, setUltima] = useState("");
  const imp = f.lines.reduce((s, l) => s + riga(l), 0);
  const iva = f.lines.reduce((s, l) => s + riga(l) * (l.aliquota || 0) / 100, 0);
  const push = (ln) => { addLine(ln); setUltima(`${ln.descrizione.slice(0, 18)} ${cents(ln.prezzo * ln.quantita)}`); kb.reset(); };
  const pick = (p) => push({ product_id: p.id, codice: p.codice || "", descrizione: p.descrizione, quantita: kb.qty, unita: p.unita || "", sconto: 0,
    prezzo: kb.buffer ? Number(kb.buffer) / 100 : p.prezzo || 0, aliquota: p.aliquota ?? 22, natura: p.natura || "" });
  const reparto = (k) => {
    const amount = Number(kb.buffer || 0) / 100;
    if (!amount) return toast.error("Digita prima l'importo, poi premi il reparto");
    const nat = k.startsWith("N");
    push({ product_id: "", codice: "", descrizione: `Reparto ${REPARTI.find((x) => x[0] === k)[1]}`, quantita: kb.qty, unita: "", sconto: 0, prezzo: amount, aliquota: nat ? 0 : Number(k), natura: nat ? k : "" });
  };
  const setQty = (i, q) => setF({ ...f, lines: q <= 0 ? f.lines.filter((_, j) => j !== i) : f.lines.map((x, j) => (j === i ? { ...x, quantita: q } : x)) });
  return (
    <div className="space-y-4" data-testid="order-pos">
      <Testata f={f} setF={setF} custs={custs} ro={ro} onNewCust={onNewCust} />
      <div className="grid gap-4 rounded-2xl border border-sky-300 bg-sky-100 p-4 shadow-xl lg:grid-cols-12">
        <div className="space-y-3 lg:col-span-4">
          <Display buffer={kb.buffer} qty={kb.qty} totale={imp + iva} ultima={ultima} />
          {!ro && <Keypad onKey={kb.key} onTimes={kb.times} onReparto={reparto} />}
          {!ro && <p className="text-xs text-slate-600">Prezzi senza IVA. Per la quantità digita il numero e premi X; un importo digitato prima di un articolo diventa il suo prezzo.</p>}
        </div>
        <div className="space-y-3 lg:col-span-4">
          {!ro && <ScanButton onCode={scan} testid="order-scan-button" />}
          <Righe lines={f.lines} ro={ro} setQty={setQty} remove={(i) => setQty(i, 0)} />
          <div className="flex justify-between rounded-lg bg-[#020617] px-4 py-2 text-sm font-semibold text-white" data-testid="order-totals">
            <span>Imponibile <span className="font-mono">{cents(imp)}</span></span><span>IVA <span className="font-mono">{cents(iva)}</span></span><span>Totale <span className="font-mono">{cents(imp + iva)}</span></span>
          </div>
          <Input disabled={ro} className="bg-white" placeholder="Note per l'ufficio" data-testid="order-notes-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </div>
        <div className="space-y-3 lg:col-span-4">
          {!ro && <Articoli products={products} onPick={pick} />}
          {!ro && <Button className="h-14 w-full rounded-xl bg-blue-700 text-lg font-bold text-white shadow-md hover:bg-blue-800" data-testid="order-save-button" disabled={busy || !f.lines.length} onClick={save}><Save className="mr-2 h-5 w-5" />Salva ordine</Button>}
          {!ro && f.lines.length > 0 && <Button variant="outline" className="w-full bg-white text-rose-700" data-testid="oc-clear-button" onClick={() => setF({ ...f, lines: [] })}>Svuota ordine</Button>}
        </div>
      </div>
    </div>
  );
};
